import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc, doc as fbDoc, getFirestore as fbGetFirestore, getDoc, getFirestore,
  onSnapshot
} from "firebase/firestore";
import { ArrowLeft, Edit, Eye, FilePlus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useToast } from "../../components/ui/use-toast";
import { app } from "../../firebase";

// Mock data for draft students
const DRAFT_STUDENTS_DATA = [
  {
    id: "DF00123",
    studentsName: "Ramesh Kumar",
    emailId: "ramesh@example.com",
    course: "Full Stack Web Development",
    createdDate: "2023-12-15",
    status: "Draft",
    mobileNumber: "+91 9876543210",
  },
  {
    id: "DF00124",
    studentsName: "Suresh Patel",
    emailId: "suresh@example.com",
    course: "Python Developer",
    createdDate: "2023-12-18",
    status: "Draft",
    mobileNumber: "+91 8765432109",
  },
  {
    id: "DF00125",
    studentsName: "Priya Sharma",
    emailId: "priya@example.com",
    course: "Java Full Stack",
    createdDate: "2023-12-20",
    status: "Draft",
    mobileNumber: "+91 7654321098",
  },
  {
    id: "DF00126",
    studentsName: "Neha Gupta",
    emailId: "neha@example.com",
    course: "React Native",
    createdDate: "2023-12-22",
    status: "Draft",
    mobileNumber: "+91 6543210987",
  },
];

const DraftList = () => {
  const [userRole, setUserRole] = useState<string>("");
    // Fetch user role
    useEffect(() => {
      const fetchUserRole = async () => {
        try {
          const auth = getAuth(app);
          const db = fbGetFirestore(app);
          const user = auth.currentUser;
          if (user) {
            const userDoc = await getDoc(fbDoc(db, "users", user.uid));
            if (userDoc.exists()) {
              setUserRole(userDoc.data().role || "");
            }
          }
        } catch (e) {
          setUserRole("");
        }
      };
      fetchUserRole();
    }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [draftStudents, setDraftStudents] = useState(DRAFT_STUDENTS_DATA);
  const [viewingStudent, setViewingStudent] = useState<any | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [deleteStudent, setDeleteStudent] = useState<any | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [draftStudentList, setDraftStudentList] = useState([]);
  const [courses, setCourses] = useState([]);
  const db = getFirestore(app);
  const auth = getAuth(app);


  const filteredStudents = draftStudentList.filter(
    (student) =>
      student.studentsName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student.emailId?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student.id?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  useEffect(() => {
    const coursesUnsubscribe = onSnapshot(
      collection(db, "courses"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,       // Add the document ID  
            ...doc.data()  // Add the document data  
          };
        });
        setCourses(result);
      },
      (error) => {
        //  addToast(error.message, "error"); // Handle errors
        //  setLoader(false); // Hide loader in case of error
      }
    );

    const draftRegistrationsSubscribe = onSnapshot(
      collection(db, "draftStudent"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,       // Add the document ID  
            ...doc.data()  // Add the document data  
          };
        });

        setDraftStudentList(result);


      },
      (error) => {
        //  addToast(error.message, "error"); // Handle errors
        //  setLoader(false); // Hide loader in case of error
      }
    );

    return () => {
      coursesUnsubscribe();
      draftRegistrationsSubscribe();
    }
  }, [])


  const handleViewStudent = (student: any) => {
    setViewingStudent(student);
    setIsViewDialogOpen(true);
  };

  const handleEditStudent = (student: any) => {
    // Navigate to draft-register page with ID
    navigate(`/students/register?id=${student.id}`);

    toast({
      title: "Editing Draft",
      description: `You are now editing draft registration for ${student.studentsName}.`,
    });
  };

  const handleDeletePrompt = (student: any) => {
    setDeleteStudent(student);
    setIsDeleteDialogOpen(true);
  };


  const handleDeleteConfirm = async () => {
    if (!deleteStudent) return;


    setIsDeleteDialogOpen(false);
    try {
      const draftDocRef = doc(db, 'draftStudent', deleteStudent?.id);
      const draftDocSnap = await getDoc(draftDocRef);
      if (draftDocSnap.exists()) {
        // Delete the draft document  
        await deleteDoc(draftDocRef);
        setDraftStudents(draftStudents.filter(student => student.id !== deleteStudent.id));
        toast({
          title: "Draft Deleted",
          description: `Draft registration for ${deleteStudent.studentsName} has been deleted.`,
        });
      }

    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while registering the student.",
      });
    }

  };

  const getCourseName = (courseId) => {
    let result = courses?.find(x => x.id == courseId)?.courseName ?? '';
    return result;
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Draft Student List" subtitle="Manage your draft student registrations" />

      <div className="mb-0 mt-6 ml-6">
        <Link to="/students" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to All Students
        </Link>
      </div>
      <div className="flex-1 p-6 animate-fade-in">
        <Card className="shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 sm:mb-0">Draft Registrations</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search drafts..."
                  className="pl-8 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {userRole === "MASTERADMIN" && (
                <Button
                  onClick={() => navigate('/students/draft-register')}
                  className="w-full sm:w-auto"
                >
                  <FilePlus className="mr-2 h-4 w-4" />
                  Create New Draft
                </Button>
              )}
            </div>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* <TableHead>ID</TableHead> */}
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Registered Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draftStudentList?.length > 0 ? (
                    filteredStudents?.map((student) => (
                      <TableRow key={student.id}>
                        {/* <TableCell className="font-medium">{student.id}</TableCell> */}
                        <TableCell>{student?.studentsName}</TableCell>
                        <TableCell>{student?.emailId}</TableCell>
                        <TableCell>{student?.mobileNumber}</TableCell>
                        <TableCell>{getCourseName(student?.courses[0]?.courseId)}</TableCell>
                        <TableCell>{new Date(student?.createdDate ? new Date(student?.createdDate?.seconds * 1000) : new Date()).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge className="bg-yellow-100 text-yellow-800">
                            {student.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleViewStudent(student)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {userRole === "MASTERADMIN" && (
                            <Button size="sm" variant="ghost" onClick={() => handleEditStudent(student)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePrompt(student)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <Search className="h-10 w-10 mb-2" />
                          <p className="text-lg">No draft registrations found</p>
                          <p className="text-sm">Try creating a new draft or adjusting your search</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Draft Student Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent style={{ maxHeight: '95vh', overflowY: 'auto', minWidth: '150px' }} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Draft Student Details</DialogTitle>
            <DialogDescription>
              Information for draft registration
            </DialogDescription>
          </DialogHeader>

          {viewingStudent && (
            <div className="grid gap-2 py-2">
              <div className="flex items-center justify-center mb-2">
                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-semibold">
                  {viewingStudent?.studentsName?.split(' ').map((n: string) => n[0]).join('')}
                </div>
              </div>

              <div className="bg-gray-50 p-1 rounded-lg border">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Student ID</p>
                    <p className="font-semibold">{viewingStudent?.id}</p>
                  </div>
                  <div className="flex items-center">
                    <p className="text-sm text-gray-500 mr-2">Status</p>
                    <Badge className="bg-yellow-100 text-yellow-800">
                      {viewingStudent.status}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 pt-2">
                <div>
                  <p className="text-sm text-gray-500">Email Address</p>
                  <p className="font-semibold">{viewingStudent.emailId}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-semibold">{viewingStudent.studentsName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-semibold">{viewingStudent.mobileNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Enrolled Course</p>
                  <p className="font-semibold">{getCourseName(viewingStudent.courses[0]?.courseId)}</p>
                </div>
              </div>
              <div className="pt-4 flex justify-end">
                <Button onClick={() => handleEditStudent(viewingStudent)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Student
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft Registration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this draft registration? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteConfirm}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DraftList;